import { useEffect, useRef } from 'react';
import type * as React from 'react';

import { useHoverWithDebounce } from '../../hooks';
import { getIdentityColor } from '../../lib/identity-palette';
import { cn } from '../../lib/utils';

export interface IdentityColumnRow {
  id: string;
  label: string;
}

export interface IdentityColumnProps {
  rows: readonly IdentityColumnRow[];
  /** Map of row id → faded boolean. Faded rows render at low opacity. */
  fadedById?: Readonly<Record<string, boolean>>;
  /** Hover-debounced enter handler; fires only after the configured delay. */
  onRowHoverEnter?: (id: string, anchorEl: HTMLElement) => void;
  onRowHoverLeave?: (id: string) => void;
  /** V3: click-to-focus. Toggles focus state for the clicked row. */
  onRowClick?: (id: string) => void;
  /** V3: id of the currently focused row (null = none). */
  focusedRowId?: string | null;
  /**
   * When false, rows render without pointer cursor and ignore clicks/hovers.
   * Used in two-axis focus to inert the column opposite the focus origin.
   */
  isInteractive?: boolean;
  /** Forwarded so the FlowLayer parent can compute pipe anchor coordinates. */
  rowRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  className?: string;
}

interface IdentityRowProps {
  row: IdentityColumnRow;
  index: number;
  isFaded: boolean;
  isFocused: boolean;
  isInteractive: boolean;
  onHoverEnter?: (id: string, anchorEl: HTMLElement) => void;
  onHoverLeave?: (id: string) => void;
  onRowClick?: (id: string) => void;
  rowRef?: (el: HTMLDivElement | null) => void;
}

function IdentityRow({
  row,
  index,
  isFaded,
  isFocused,
  isInteractive,
  onHoverEnter,
  onHoverLeave,
  onRowClick,
  rowRef,
}: IdentityRowProps) {
  const hover = useHoverWithDebounce();
  const color = getIdentityColor(index);
  const labelRef = useRef<HTMLSpanElement>(null);
  const onHoverEnterRef = useRef(onHoverEnter);
  onHoverEnterRef.current = onHoverEnter;

  useEffect(() => {
    // Anchor the hover popover to the label text, as on the data-context side.
    if (hover.isHovered && labelRef.current) {
      onHoverEnterRef.current?.(row.id, labelRef.current);
    }
  }, [hover.isHovered, row.id]);

  const handleMouseLeave = () => {
    hover.onMouseLeave();
    onHoverLeave?.(row.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isInteractive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row.id);
    }
  };

  const cursorClass = isInteractive ? 'cursor-pointer' : 'cursor-default';
  const onMouseEnter = isInteractive ? hover.onMouseEnter : undefined;
  const onMouseLeaveHandler = isInteractive ? handleMouseLeave : undefined;

  return (
    <div
      id={`identity-row-${row.id}`}
      data-testid={`identity-row-${row.id}`}
      data-row-id={row.id}
      ref={rowRef}
      className={cn('flex items-center justify-end transition-opacity', isFaded && 'opacity-20')}
    >
      <div
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-pressed={isInteractive ? isFocused : undefined}
        aria-label={isInteractive ? row.label : undefined}
        data-row-hit-area="true"
        className="pointer-events-none flex items-center gap-3"
        onClick={isInteractive ? () => onRowClick?.(row.id) : undefined}
        onKeyDown={handleKeyDown}
      >
        <span
          ref={labelRef}
          className={cn(
            'pointer-events-auto text-right text-[12px] font-medium leading-[1.4] text-dawn-black',
            cursorClass
          )}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeaveHandler}
        >
          {row.label}
        </span>
        <span
          aria-hidden="true"
          data-flow-bar="source"
          className={cn('pointer-events-auto h-10 w-2 shrink-0 rounded-[2px]', cursorClass)}
          style={{ backgroundColor: color }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeaveHandler}
        />
      </div>
    </div>
  );
}

export function IdentityColumn({
  rows,
  fadedById,
  onRowHoverEnter,
  onRowHoverLeave,
  onRowClick,
  focusedRowId,
  isInteractive = true,
  rowRefs,
  className,
}: IdentityColumnProps) {
  return (
    <div data-testid="identity-column" className={cn('flex flex-col gap-[10px]', className)}>
      {rows.map((row, index) => (
        <IdentityRow
          key={row.id}
          row={row}
          index={index}
          isFaded={fadedById?.[row.id] === true}
          isFocused={focusedRowId === row.id}
          isInteractive={isInteractive}
          onHoverEnter={onRowHoverEnter}
          onHoverLeave={onRowHoverLeave}
          onRowClick={onRowClick}
          rowRef={
            rowRefs
              ? (el) => {
                  rowRefs.current[row.id] = el;
                }
              : undefined
          }
        />
      ))}
    </div>
  );
}

IdentityColumn.displayName = 'IdentityColumn';
